import { useContext, useMemo } from 'react';
import Link from 'next/link';
import { Star, Tag } from 'lucide-react';
import { AppContext } from 'contexts/';
import { CURRENCY } from 'constants/';
import { Quantico } from 'next/font/google';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import Image from '../../../Image';
import styles from './index.module.scss';

const quantico = Quantico({
  weight: '400',
  subsets: ['latin'],
});

interface ItemProps extends Product.Item {
  href: string;
  handleClose: () => void;
}

const Item = ({ assets, discount, price, discountPrice, name, category, outOfStock, reviews, href, handleClose }: ItemProps) => {
  const { currency } = useContext(AppContext);
  const { t } = useTranslation('header');
  const imgSrc = assets?.items?.find((a) => a.type === 'img')?.src || '/notFound.png';

  const avgRating = useMemo(() => {
    if (!reviews?.length) return null;
    return (reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length).toFixed(1);
  }, [reviews]);

  return (
    <Link href={href} className={styles.item} onClick={handleClose}>
      <div className={styles.imageWrap}>
        <Image
          className={styles.image}
          src={imgSrc}
          alt={name}
          width={56}
          height={56}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {discount > 0 && (
        <span className={styles.discountBadge}>
          -
          {discount}
          %
        </span>
        )}
        {outOfStock && <span className={styles.outOfStockOverlay}>{t('search.outOfStock')}</span>}
      </div>

      <div className={styles.info}>
        {category && (
          <span className={styles.category}>
            <Tag size={11} />
            {category}
          </span>
        )}
        <span className={styles.name}>{name}</span>
        {avgRating && (
          <span className={styles.rating}>
            <Star size={12} className={styles.star} />
            {avgRating}
            {reviews?.length ? (
              <span className={styles.reviewsCount}>
                (
                {reviews.length}
                )
              </span>
            ) : null}
          </span>
        )}
      </div>

      <div className={classNames(styles.priceBlock, quantico.className)}>
        {discount > 0 ? (
          <>
            <span className={styles.price}>
              {CURRENCY[currency].sign}
              {(discountPrice || +price.value).toFixed(2)}
            </span>
            <span className={styles.oldPrice}>
              {CURRENCY[currency].sign}
              {(+price.value).toFixed(2)}
            </span>
          </>
        ) : (
          <span className={styles.price}>
            {CURRENCY[currency].sign}
            {(+price.value).toFixed(2)}
          </span>
        )}
        {outOfStock && <span className={styles.outOfStockPill}>{t('search.outOfStock')}</span>}
      </div>
    </Link>
  );
};

export default Item;
